import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { fetcher } from './api';

/**
 * Standard API hook wrapping SWR
 * Returns data, meta, error, isLoading, isValidating, mutate
 */
export function useApi(path, options = {}) {
  const { data: response, error, isLoading, isValidating, mutate } = useSWR(
    path ? path : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
      dedupingInterval: 60000,
      errorRetryCount: 2,
      ...options
    }
  );

  return {
    data: response?.data !== undefined ? response.data : response,
    meta: response?.meta,
    raw: response,
    error,
    isLoading,
    isValidating,
    mutate
  };
}

/**
 * Cached API hook for stable data (classes, subjects, brand, timetable, settings)
 * 5-minute deduping interval to eliminate redundant requests across pages.
 */
export function useCachedApi(path, options = {}) {
  return useApi(path, {
    dedupingInterval: 300000,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    ...options
  });
}

/**
 * Polling API hook with pause on hidden tab (opt-in only, default interval 0)
 */
export function usePolling(path, ms = 0, options = {}) {
  return useApi(path, {
    refreshInterval: ms,
    refreshWhenHidden: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    ...options
  });
}

/**
 * Mutation helper hook
 */
export function useMutation(fn) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const run = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fn(...args);
      setData(res);
      return res;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fn]);

  const reset = useCallback(() => {
    setError(null);
    setData(null);
    setLoading(false);
  }, []);

  return { run, loading, error, data, reset };
}

export { useGuardedAction } from '@/admin/useGuardedAction';
